import React from 'react';
import { MapIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HTip, HBold } from '../HandbookContent';

export default function ChapterWhatIsPlaybook() {
  return (
    <HChapter
      title="What is a Playbook?"
      subtitle="The big picture: how EdgePlaybook turns your legal processes into reliable, repeatable workflows."
    >
      <HMetaphor
        icon={<MapIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of it as a Highway Map"
      >
        Imagine your legal matter is a road trip. A playbook is the map that shows every possible route
        from the starting line (case opened) to the finish (case closed). Every stop along the way is
        a <strong>status</strong>. Every on-ramp or off-ramp is a <strong>transition</strong>. You always
        know exactly where you are and where you can go next.
      </HMetaphor>

      <img
        src="/handbook/visual-editor-overview.svg"
        alt="Visual Editor canvas showing a family law workflow with five status nodes (Case Opened, Filed, Served, Discovery, Closed) connected by transition arrows, with the editor toolbar above and property panel sidebar on the right"
        className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
      />

      <HSection title="In Plain English">
        <HParagraph>
          A playbook is a <HBold>workflow template</HBold>. It defines the sequence of steps that a legal
          task or matter follows from beginning to end. Instead of keeping these steps in your head or on
          a sticky note, EdgePlaybook makes them visible, enforceable, and trackable.
        </HParagraph>
        <HParagraph>
          Every playbook has three core ingredients:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-1">
          <li><HBold>Statuses</HBold> — the stops along the route (e.g., "Filed," "Served," "Discovery," "Trial")</li>
          <li><HBold>Transitions</HBold> — the allowed moves between stops (e.g., from "Filed" you can move to "Served")</li>
          <li><HBold>Rules</HBold> — conditions that must be met before a move can happen (e.g., proof of service must be uploaded)</li>
        </ul>
      </HSection>

      <HSection title="Why It Matters for Your Firm">
        <HParagraph>
          Without a playbook, every paralegal or associate might handle the same type of case differently.
          Deadlines get missed. Steps get skipped. With a playbook, the system enforces your firm's best
          practices automatically — every case follows the same proven path.
        </HParagraph>
        <HParagraph>
          Think of it this way: a playbook is like your firm's <HBold>institutional knowledge</HBold>,
          captured in a format that the system can enforce. When a junior associate joins, they don't need
          to memorize every step — the playbook guides them through it.
        </HParagraph>
      </HSection>

      <HSection title="System Playbooks vs. Firm Playbooks">
        <HParagraph>
          EdgePlaybook ships with <HBold>system playbooks</HBold> — pre-built workflow templates for
          common practice areas like family law, civil litigation, and estate planning. These are read-only
          templates maintained by EdgeLex.
        </HParagraph>
        <HParagraph>
          Your firm creates <HBold>firm playbooks</HBold> by cloning a system playbook and customizing it.
          This way, you start with a proven foundation and tailor it to your firm's specific procedures.
        </HParagraph>
      </HSection>

      <HTip>
        You don't need to build playbooks from scratch. Start by cloning a system playbook from the
        Library and then adjust it to match your firm's workflow. It's the fastest way to get up and running.
      </HTip>
    </HChapter>
  );
}
